/**
 * The handful of numbers people actually compare strains on.
 *
 * These live in three places that all need the same reading of them — the
 * comparison table, `llms.txt`, and the `additionalProperty` block in
 * `$lib/seo/schema` — so the extraction happens once here rather than three
 * times with three slightly different ideas of what "10" means.
 *
 * Values come back **raw**, in the frontmatter's own vocabulary (`very_high`,
 * `hybrid`), not localised. Localisation belongs to whatever renders them: the
 * table shows the reader's language, the structured data deliberately shows
 * English (see `PROPERTY_NAMES` in `$lib/seo/schema`), and `llms.txt` shows
 * English because it is one document describing eight locales.
 */

import type { Product } from './products';

export interface StrainFacts {
	/** `indica` | `sativa` | `hybrid`, as written in the strain frontmatter. */
	type?: string;
	/** `low` | `high` | `very_high` | `extremely_high`. */
	thc?: string;
	cbd?: string;
	/** A t-shirt size — `L`, `XL`, `XXL`. Not a gram figure. */
	yieldSize?: string;
	/**
	 * Flowering time exactly as written: `"8"`, but also `"10-11"` and `"9 - 10"`.
	 *
	 * Kept as the string for display, because half the catalogue gives a range and
	 * flattening those to a single number would publish a precision the breeder
	 * did not claim. `harvestWeeksRange` is the parsed form, for sorting.
	 */
	harvestWeeks?: string;
	/** The same value parsed, for comparison. Equal bounds when it is one number. */
	harvestWeeksRange?: { min: number; max: number };
	family?: string;
}

/**
 * Reads `"8"`, `"10-11"` and `"9 - 10"` alike.
 *
 * A plain `parseFloat` silently turns `"10-11"` into `10`, which is how a range
 * becomes a false precise claim. Anything with more than two numbers in it is
 * refused rather than guessed at.
 */
export function parseWeeks(raw: string | undefined): { min: number; max: number } | undefined {
	if (!raw) return undefined;
	const numbers =
		raw
			.match(/\d+(?:\.\d+)?/g)
			?.map(Number)
			.filter(Number.isFinite) ?? [];
	if (numbers.length === 1) return { min: numbers[0], max: numbers[0] };
	if (numbers.length === 2) return { min: Math.min(...numbers), max: Math.max(...numbers) };
	return undefined;
}

type Attributes = Pick<Product, 'attributes'>;

function attribute(product: Attributes, key: string): string | undefined {
	const found = product.attributes?.find((entry) => entry.key === key)?.value;
	return found === undefined || found === '' ? undefined : String(found);
}

export function strainFacts(product: Attributes): StrainFacts {
	const harvestWeeks = attribute(product, 'harvestWeeks');

	return {
		type: attribute(product, 'type'),
		thc: attribute(product, 'thc'),
		cbd: attribute(product, 'cbd'),
		yieldSize: attribute(product, 'yieldSize'),
		harvestWeeks,
		harvestWeeksRange: parseWeeks(harvestWeeks),
		family: attribute(product, 'family')
	};
}

/** Pack sizes on sale, ascending. From WooCommerce, never the `.svx` list. */
export const packSizes = (product: Pick<Product, 'prices'>): number[] =>
	product.prices.map((pack) => pack.size);

/**
 * The pack that works out cheapest per seed, and what that per-seed price is.
 *
 * This is the comparison the pack sizes exist to make and the one the site could
 * not previously answer anywhere: a 100-seed pack of Auto Critical Orange Punch
 * is the most expensive line on the page and the cheapest seed on it. Rounded to
 * two decimals because it is money, and computed rather than stored because a
 * stored copy would drift from the prices beside it.
 */
export function bestPricePerSeed(
	product: Pick<Product, 'prices'>
): { size: number; pricePerSeed: number } | undefined {
	const best = product.prices
		.filter((pack) => pack.size > 0)
		.map((pack) => ({ size: pack.size, pricePerSeed: pack.price / pack.size }))
		.sort((a, b) => a.pricePerSeed - b.pricePerSeed)[0];

	return best
		? { size: best.size, pricePerSeed: Math.round(best.pricePerSeed * 100) / 100 }
		: undefined;
}

/**
 * How strong the THC ratings are, relative to each other.
 *
 * The frontmatter records a band, not a percentage — Dutch Passion publishes no
 * per-strain THC figure for the Thai catalogue, and inventing one to make the
 * column sortable would be exactly the wrong trade. This orders the bands so
 * "which is the strongest" has an answer without pretending to a number.
 */
export const THC_ORDER = ['low', 'high', 'very_high', 'extremely_high'] as const;

export const thcRank = (thc: string | undefined): number =>
	thc ? THC_ORDER.indexOf(thc as (typeof THC_ORDER)[number]) : -1;

/**
 * Yield sizes, smallest first.
 *
 * Only `L`, `XL` and `XXL` appear in the current catalogue; the smaller rungs
 * are listed so a strain added later does not sort to the bottom for being
 * unrecognised.
 */
export const YIELD_ORDER = ['XS', 'S', 'M', 'L', 'XL', 'XXL'] as const;

export const yieldRank = (size: string | undefined): number =>
	size ? YIELD_ORDER.indexOf(size as (typeof YIELD_ORDER)[number]) : -1;
